<template>
    <article class="bio-analyse" :aria-labelledby="uid + '-titre'" :aria-busy="is_saving">
        <header class="bio-analyse-entete">
            <h3 :id="uid + '-titre'">{{ numero }}. {{ ligne.analyse }}</h3>
            <span v-if="ligne.est_urgent === 1" class="bio-badge bio-badge-danger">Urgent</span>
            <span v-if="traite" class="bio-badge" role="status">{{ statut_final }}</span>
            <small v-if="ligne.resultat">Version {{ ligne.resultat.version }}</small>
        </header>
        <p class="bio-trace">Saisi le {{ date_heure(ligne.saisi_at) }}<template v-if="ligne.saisi_par_nom"> par {{ ligne.saisi_par_nom }}</template><template v-if="ligne.echantillons && ligne.echantillons.length"> · {{ ligne.echantillons.join(', ') }}</template><template v-if="ligne.technique_utilisee"> · {{ ligne.technique_utilisee }}</template></p>
        <div v-if="lecture.length" class="bio-resultats-scroll" tabindex="0" :aria-label="'Résultats de ' + ligne.analyse">
            <table class="bio-resultats">
                <thead><tr><th>Paramètre</th><th>Résultat</th><th>Unité</th><th>Référence</th><th><span class="sr-only">Alerte</span></th></tr></thead>
                <tbody><tr v-for="(item, index) in lecture" :key="index">
                    <td>{{ item.libelle }}<small v-if="item.section && item.section !== 'Résultat'">{{ item.section }}</small></td>
                    <td><strong>{{ item.valeur }}</strong></td><td>{{ item.unite }}</td><td>{{ item.reference }}</td>
                    <td><span v-if="item.alerte" class="bio-badge" :class="'bio-badge-' + item.alerte.tone">{{ item.alerte.label }}</span></td>
                </tr></tbody>
            </table>
        </div>
        <p v-if="!lecture.length && !documents.length" class="text-danger">Aucun résultat lisible pour cette analyse.</p>
        <p v-if="ligne.resultat && ligne.resultat.comment" class="bio-commentaire"><strong>Commentaire du technicien : </strong>{{ ligne.resultat.comment }}</p>
        <ul v-if="documents.length" class="bio-documents">
            <li v-for="doc in documents" :key="doc.id">{{ doc.name }} <small>v{{ doc.version }}<template v-if="doc.uploaded_by_name"> · {{ doc.uploaded_by_name }}</template></small> <button type="button" class="bio-lien" :disabled="is_saving" @click="ouvrir_document(doc)">Voir le document</button></li>
        </ul>
        <template v-if="!traite">
            <fieldset :disabled="verrouille" class="bio-champs">
                <div v-if="valeurs_critiques.length" class="bio-critique" role="alert">
                    <strong>Valeur critique — tracer l’alerte avant diffusion</strong>
                    <ul><li v-for="(valeur, index) in valeurs_critiques" :key="index">{{ valeur.label }} : {{ valeur.value }} {{ valeur.unit }} ({{ valeur.sens === 'bas' ? 'inférieur à ' + valeur.min : 'supérieur à ' + valeur.max }})</li></ul>
                    <div class="bio-grille">
                        <label :for="uid + '-prevenu'">Personne prévenue<input :id="uid + '-prevenu'" v-model="alerte.personne_prevenue" type="text" maxlength="150" placeholder="Prescripteur ou service prévenu"></label>
                        <label :for="uid + '-heure'">Prévenue le<input :id="uid + '-heure'" v-model="alerte.prevenu_at" type="datetime-local"></label>
                    </div>
                </div>
                <div class="bio-interpretation-entete">
                    <label :for="uid + '-interpretation'">Interprétation du biologiste</label>
                    <BoutonDictee v-if="!verrouille" ref="dictee" :pour="uid + '-interpretation'" />
                </div>
                <textarea :id="uid + '-interpretation'" v-model="interpretation" rows="2" maxlength="4000" placeholder="Interprétation, conclusion…"></textarea>
                <div v-if="retour_en_cours" class="bio-retour">
                    <div class="bio-grille">
                        <label :for="uid + '-destination'">Renvoyer vers
                            <select :id="uid + '-destination'" :value="retour ? 'saisie' : 'technique'" @change="choisir_retour($event.target.value)">
                                <option value="saisie">Saisie — corriger le résultat</option><option value="technique">Technique — refaire un contrôle</option>
                            </select>
                        </label>
                        <label :for="uid + '-motif'">Motif obligatoire<input :id="uid + '-motif'" v-model="motif" type="text" maxlength="500" placeholder="Précisez ce qui doit être corrigé ou contrôlé"></label>
                    </div>
                    <template v-if="!retour">
                        <label class="bio-checkbox"><input type="checkbox" :checked="retour_technique === 'non_conformite'" @change="declarer_nc($event.target.checked)"> Déclarer aussi une non-conformité au registre qualité</label>
                        <label v-if="retour_technique === 'non_conformite'" :for="uid + '-categorie'">Catégorie obligatoire
                            <select :id="uid + '-categorie'" v-model="categorie_non_conformite"><option value="">Choisir une catégorie</option><option value="identification">Identification</option><option value="prelevement">Prélèvement</option><option value="transport">Transport / conservation</option><option value="delai">Délai</option><option value="equipement">Équipement / réactif</option><option value="procedure">Procédure</option><option value="resultat">Résultat</option><option value="autre">Autre</option></select>
                        </label>
                    </template>
                    <p>Le résultat ne sera pas diffusé.{{ interpretation.trim() ? ' L’interprétation en cours ne sera pas enregistrée lors du retour.' : '' }}</p>
                </div>
                <div v-if="confirmation" class="bio-confirmation" role="alert">
                    Diffuser le résultat de <strong>{{ ligne.analyse }}</strong> au prescripteur et au compte rendu ?
                </div>
            </fieldset>
            <p v-if="erreur_modal" class="text-danger bio-erreur" role="alert">{{ erreur_modal }}</p>
            <div class="ui-modal-actions bio-actions">
                <template v-if="retour_en_cours">
                    <button type="button" class="btn btn-secondary-light-o" :disabled="verrouille" @click="annuler_retour">Annuler le retour</button>
                    <button type="button" class="btn btn-outline-danger" :disabled="verrouille" @click="confirmer_retour">{{ is_saving ? 'Enregistrement…' : 'Confirmer le retour' }}</button>
                </template>
                <template v-else-if="confirmation">
                    <button type="button" class="btn btn-secondary-light-o" :disabled="verrouille" @click="confirmation = false">Continuer la relecture</button>
                    <button type="button" class="btn btn-success" :disabled="verrouille" @click="confirmer_validation">{{ is_saving ? 'Enregistrement…' : 'Confirmer et diffuser' }}</button>
                </template>
                <template v-else>
                    <button type="button" class="btn btn-secondary-light-o" :disabled="verrouille" @click="choisir_retour('technique')">Renvoyer…</button>
                    <button type="button" class="btn btn-success" :disabled="verrouille || (!lecture.length && !documents.length)" @click="demander_validation">Valider et diffuser</button>
                </template>
            </div>
        </template>
        <p v-else-if="interpretation && statut_final === 'Validé et diffusé'" class="bio-commentaire"><strong>Interprétation : </strong>{{ interpretation }}</p>
    </article>
</template>

<script>
import RelectureAnalyse from './relecture-analyse'
import BoutonDictee from '@/components/dictee/bouton-dictee.vue'

export default {
    name: 'RelectureAnalyse',
    components: { BoutonDictee },
    mixins: [RelectureAnalyse],
    props: { ligne: { type: Object, required: true }, numero: { type: Number, required: true }, busy: Boolean },
    data: () => ({ traite: false, confirmation: false, statut_final: '' }),
    computed: {
        uid() { return 'bio-relecture-' + this._uid },
        verrouille() { return this.is_saving === true || this.busy === true || this.traite === true },
        motif: {
            get() { return this.retour ? this.motif_retour : this.motif_retour_technique },
            set(value) { if (this.retour) this.motif_retour = value; else this.motif_retour_technique = value }
        }
    },
    watch: { is_saving(value) { this.$emit('busy', value) } },
    created() { this.ouvrir(this.ligne) },
    methods: {
        hasDraft() {
            return !this.traite && (this.interpretation !== (this.ligne.interpretation_biologiste || '') ||
                !!(this.motif_retour || this.motif_retour_technique || this.categorie_non_conformite || this.alerte.personne_prevenue || this.alerte.prevenu_at))
        },
        arreter_dictee() { if (this.$refs.dictee) this.$refs.dictee.arreter() },
        choisir_retour(destination) {
            if (this.verrouille) return
            this.arreter_dictee()
            this.confirmation = false
            this.erreur_modal = ''
            const motif = this.motif
            this.retour = destination === 'saisie'
            this.retour_technique = this.retour ? '' : 'controle'
            this.categorie_non_conformite = ''
            this.motif = motif
        },
        declarer_nc(value) {
            if (this.verrouille || this.retour) return
            this.retour_technique = value ? 'non_conformite' : 'controle'
            if (!value) this.categorie_non_conformite = ''
        },
        demander_validation() {
            if (this.verrouille || this.retour_en_cours) return
            this.arreter_dictee()
            this.erreur_modal = ''
            this.confirmation = true
        },
        confirmer_validation() {
            if (this.verrouille || !this.confirmation || this.retour_en_cours) return Promise.resolve()
            if (this.valeurs_critiques.length && (!this.alerte.personne_prevenue.trim() || !this.alerte.prevenu_at)) {
                this.erreur_modal = 'Renseignez la personne prévenue et la date de l’alerte avant de diffuser ce résultat critique.'
                return Promise.resolve()
            }
            return this.valider()
        },
        confirmer_retour() {
            if (this.verrouille || !this.retour_en_cours) return Promise.resolve()
            return this.retour ? this.renvoyer() : this.renvoyer_technique()
        },
        apres_action() {
            this.statut_final = this.retour ? 'Renvoyé à la saisie' : this.retour_technique === 'non_conformite' ? 'Renvoyé en technique · NC déclarée' : this.retour_technique ? 'Renvoyé en technique' : 'Validé et diffusé'
            this.traite = true
            this.confirmation = false
            this.$emit('saved', this.info)
        }
    }
}
</script>

<style scoped lang="scss">
.bio-analyse { border: 1px solid #d6dff2; border-radius: 14px; background: white; padding: 12px 14px; margin-bottom: 10px; color: var(--ui-text, #172033); font-size: 14px; }
.bio-analyse-entete { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px; }
.bio-analyse-entete h3 { margin: 0; font-size: 16px; font-weight: 700; }
.bio-analyse-entete small, .bio-trace { color: var(--ui-text-muted, #526074); font-size: 12px; }
.bio-trace { margin: 5px 0 10px; }
.bio-resultats-scroll { position: relative; overflow-x: auto; }
.bio-resultats { width: 100%; min-width: 560px; border-collapse: collapse; font-size: 13px; }
.bio-resultats th, .bio-resultats td { padding: 7px 9px; text-align: left; border: 1px solid #d6dff2; overflow-wrap: anywhere; vertical-align: top; }
.bio-resultats th { background: #f4f7fc; font-weight: 600; }
.bio-resultats th:first-child { width: 25%; }
.bio-resultats th:nth-child(4) { width: 40%; }
.bio-resultats small { display: block; color: var(--ui-text-muted, #526074); }
.bio-badge { display: inline-block; border-radius: 8px; padding: 3px 7px; font-size: 12px; background: #e8f7ef; color: #166044; }
.bio-badge-danger { background: #fdecec; color: #982f29; }
.bio-badge-warning { background: #fff4d8; color: #79520a; }
.bio-champs { border: 0; margin: 0; padding: 0; min-width: 0; }
.bio-interpretation-entete { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 14px; margin: 8px 0 6px; }
.bio-interpretation-entete label { margin: 0; font-weight: 600; }
.bio-champs textarea, .bio-champs input:not([type=checkbox]), .bio-champs select { display: block; width: 100%; min-height: 38px; padding: 7px 10px; border: 1px solid #9aaac1; border-radius: 9px; background: white; color: var(--ui-text, #172033); font: inherit; }
.bio-champs textarea { resize: vertical; min-height: 64px; }
.bio-champs label { font-weight: 600; }
.bio-champs input:focus-visible, .bio-champs textarea:focus-visible, .bio-champs select:focus-visible, button:focus-visible, .bio-resultats-scroll:focus-visible { outline: 2px solid var(--ui-accent, #2457c5); outline-offset: 2px; }
.bio-grille { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.bio-grille label { margin: 0; }
.bio-grille input, .bio-grille select { margin-top: 4px; }
.bio-retour, .bio-critique, .bio-confirmation { margin-top: 10px; padding: 10px 12px; border: 1px solid #d6dff2; border-radius: 10px; background: #f4f7fc; }
.bio-retour p { margin: 8px 0 0; font-size: 12px; color: var(--ui-text-muted, #526074); }
.bio-critique { background: #fff2f0; border-color: #edc5c1; }
.bio-critique ul { margin: 4px 0 8px; padding-left: 18px; }
.bio-checkbox { display: flex; align-items: center; gap: 8px; margin: 10px 0; }
.bio-checkbox input { width: 18px; height: 18px; flex-shrink: 0; }
.bio-actions { margin-top: 10px; }
.bio-commentaire, .bio-documents, .bio-erreur { margin: 8px 0 0; font-size: 13px; }
.bio-documents { padding-left: 18px; }
.bio-lien { border: 0; background: transparent; color: var(--ui-accent, #2457c5); text-decoration: underline; }
@media (max-width: 600px) { .bio-grille { grid-template-columns: 1fr; } .bio-analyse { padding: 10px; } }
</style>
